'use client';

import { ChevronDownIcon } from '@/components/icons/chevron-down-icon';
import { cn } from '@/lib/utils';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as React from 'react';

const Accordion = ({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>): React.JSX.Element => {
  return <AccordionPrimitive.Root data-slot="accordion"
    {...props}
  />;
};

const AccordionItem = ({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>): React.JSX.Element => {
  return (
    <AccordionPrimitive.Item className={cn('border-b last:border-b-0 flex flex-col gap-[20px]', className)}
      data-slot="accordion-item"
      {...props}
    />
  );
};

const AccordionTrigger = ({
  children,
  className,
  iconClassName,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  iconClassName?: string;
}): React.JSX.Element => {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger className={cn(
        'focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md text-left text-sm font-normal',
        'transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
        className
      )}
      data-slot="accordion-trigger"
      {...props}
      >
        {children}
        <ChevronDownIcon className={cn(
          'text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200',
          iconClassName
        )}
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
};

const AccordionContent = ({
  children,
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>): React.JSX.Element => {
  return (
    <AccordionPrimitive.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm"
      data-slot="accordion-content"
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
};

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
